import BaseComponent from "../core/base-component.js";
import * as TypeConverter from "../utilities/type-converter.js";
import ShowPasswordWidget from "./widget/show-password-widget.js";
import CounterWidget from "./widget/counter-widget.js";
import PasswordStrengthWidget from "./widget/password-strength-widget.js";
import ValuePickerWidget from "./widget/value-picker-widget.js";
import FileUploadWidget from "./widget/file-upload-widget.js";
import RangeWidget from "./widget/range-widget.js";
import RatingGradingWidget from "./widget/rating-grading-widget.js";
import SelectListWidget from "./widget/select-list-widget.js";
import ScrollTo from "../component/scroll-to.js";
import type { KeyValuePair } from "../types/index.js";

interface ProcessState {
    widgets: any[]
}

interface FormConfig {
    validateOnSubmit: boolean;
    locale: string;
}

interface Validation {
    listeners?: string[];
    validators: Validator[]
}

interface Validator {
    identifier: string;
    validateOnChange: boolean;
    options?: { [key: string]: string };
}
interface FileUploadValidator extends Validator {
    fileSize: number;
    accept: string;
}


const defaultOptions: FormConfig = {
    validateOnSubmit: true,
    locale: ''
};

const fieldValidationMap = new Map<HTMLElement, Validation>();

class Form extends BaseComponent {
    protected form: HTMLFormElement;
    protected options: FormConfig;
    protected processState: ProcessState = {
        widgets: []
    };

    public override init() {
        this.form = this.element as HTMLFormElement;
        this.options = this.assignConfig<FormConfig>(defaultOptions, this.config);

        const lang = document.querySelector('html')?.getAttribute('lang');
        this.options.locale = (lang == 'de') ? 'de' : '';
    }

    public override process() {
        this.processFields();
        this.form.setAttribute('novalidate', 'novalidate');
        this.form.addEventListener('submit', (ev: SubmitEvent, args: any = null) => {
            let isValid: boolean = true;
            if(
                ( ev.submitter && (ev.submitter as HTMLButtonElement).formNoValidate )
                || (ev.target && (ev.target as HTMLFormElement).ignoreValidation )
            ) {
                return isValid;
            }
            if(
                (args && args.ignoreValidation)
                || (args && args.noValidation)
                || this.form.ignoreValidation
                || this.form.noValidation
            ) {
                return isValid;
            }
            this.form.querySelectorAll<HTMLInputElement>('input,textarea,select').forEach((el: HTMLInputElement) => {
                try {
                    const isFieldValid = this.validate(el);
                    if(!isFieldValid) {
                        isValid = false;
                    }
                } catch {
                    isValid = false;
                }
            });
            if(!isValid) {
                this.handleValidationResult();
                ev.preventDefault();
                ev.stopPropagation();
            }
        });

        if(this.processState.widgets.length > 0) {
            this.renderWidgets(this.processState.widgets);
        }
    }

    protected handleValidationResult() {
        const firstElement: HTMLElement = this.form.querySelector<HTMLElement>('.is-invalid')!;
        if(firstElement) {
            ScrollTo.scrollTo(firstElement.closest<HTMLElement>('.form-element'));
            window.setTimeout(function() {
                firstElement.focus();
            }, 0);
        }

        window.ThiemeMaintemplate.toast(
            window.ThiemeCore.Lang.translate('common:validationError.title'),
            window.ThiemeCore.Lang.translate('common:validationError.message'),
            {
                className: 'alert alert-danger',
                heading: {
                    className: 'alert-heading d-flex mb-3'
                },
                body: {
                    className: 'alert-body'
                },
                delay: 20000
            }
        );
    }

    protected processFields() {
        this.form.querySelectorAll<HTMLInputElement>('input,textarea,select').forEach((fieldEl: HTMLInputElement) => {
            let fieldType = fieldEl.tagName.toUpperCase();
            if(fieldEl.getAttribute('type') == 'datetime') {
                fieldEl.type = 'datetime-local';
            }
            if(fieldType == 'INPUT' && fieldEl.type) {
                fieldType += ':' + fieldEl.type.toUpperCase();
            }
            if(
                fieldEl.classList.contains('datefield')
                || fieldEl.classList.contains('dateselector')
                || fieldEl.classList.contains('datepicker')
                || fieldEl.dataset.t3FormDatepicker != null
                || (fieldEl.dataset.wizard && fieldEl.dataset.wizard == 'datepicker')
            ) {
                fieldType = 'INPUT:DATE';
            }
            if(
                fieldEl.classList.contains('datetimefield')
                || fieldEl.classList.contains('datetimeselector')
                || fieldEl.classList.contains('datetimepicker')
                || (fieldEl.dataset.wizard && fieldEl.dataset.wizard == 'datetimepicker')
            ) {
                fieldType = 'INPUT:DATETIME';
            }
            if(
                fieldEl.classList.contains('timefield')
                || fieldEl.classList.contains('timeselector')
                || fieldEl.classList.contains('timepicker')
                || (fieldEl.dataset.wizard && fieldEl.dataset.wizard == 'timepicker')
            ) {
                fieldType = 'INPUT:TIME';
            }


            if(fieldEl.dataset.renderType) {
                fieldType += ':' + fieldEl.dataset.renderType.toUpperCase();
            }

            switch(fieldType) {
                case 'INPUT:PASSWORD':
                    if(fieldEl.dataset.pvs && parseInt(fieldEl.dataset.pvs) > 0) {
                        this.processState.widgets.push({
                            identifier: 'showHidePassword',
                            field: fieldEl
                        });
                    }
                break;
                // Date field
                case 'INPUT:DATE':
                    this.processState.widgets.push({
                        identifier: 'datePicker',
                        field: fieldEl,
                        format: fieldEl.dataset.format ?? 'Y-m-d',
                        enableDatePicker: true,
                        displayTimeSelector: false
                    });
                break;
                // Datetime field
                case 'INPUT:DATETIME':
                case 'INPUT:DATETIME-LOCAL':
                    this.processState.widgets.push({
                        identifier: 'datePicker',
                        field: fieldEl,
                        format: fieldEl.dataset.format ?? 'Y-m-dTH:i:sP',
                        enableDatePicker: true,
                        displayTimeSelector: true
                    });
                break;
                // Time field
                case 'INPUT:TIME':
                    this.processState.widgets.push({
                        identifier: 'datePicker',
                        field: fieldEl,
                        format: fieldEl.dataset.format ?? 'H:i',
                        enableDatePicker: false,
                        displayTimeSelector: true
                    });
                break;
                case 'INPUT:RANGE':
                    this.processState.widgets.push({
                        identifier: 'range',
                        field: fieldEl
                    });
                break;
                case 'INPUT:RATING':
                case 'INPUT:TEXT:RATING':
                    this.processState.widgets.push({
                        identifier: 'rating',
                        field: fieldEl
                    });
                break;
                case 'INPUT:GRADING':
                case 'INPUT:TEXT:GRADING':
                    this.processState.widgets.push({
                        identifier: 'grading',
                        field: fieldEl
                    });
                break;
                case 'SELECT:SELECTLIST':
                    this.processState.widgets.push({
                        identifier: 'selectList',
                        field: fieldEl
                    });
                break;
                // Upload field
                case 'INPUT:FILE':
                    const validator: FileUploadValidator = {
                        identifier: 'fileUpload',
                        accept: fieldEl.getAttribute('accept') ?? '',
                        fileSize: parseInt(fieldEl.dataset.fileSize ?? (1048576 * 2).toString()),
                        validateOnChange: true
                    };

                    const validation: Validation = {
                        validators: [ validator ]
                    };

                    fieldValidationMap.set(fieldEl, validation);
                    this.processState.widgets.push({
                        identifier: 'fileUpload',
                        field: fieldEl,
                    });
                break;
                case 'TEXTAREA':
                    // RTE field
                    if(
                        (
                            fieldEl.classList.contains('enable-rte')
                            || fieldEl.dataset.enableRichtext
                            || fieldEl.dataset.enableRte
                        )
                    ) {
                        this.processState.widgets.push({
                            identifier: 'ckeditor',
                            field: fieldEl
                        })
                    }
                break;
            }

            // Clearbutton
            const clearableFieldTypes = ',INPUT:TEXT,INPUT:TEL,INPUT:NUMBER,INPUT:SEARCH,INPUT:EMAIL,INPUT:URL,';
            if(fieldEl.parentElement?.classList.contains('clearable') && clearableFieldTypes.search(','+fieldType+',') > -1 && parseInt(fieldEl.style.marginLeft) > -1) {
                if(!fieldEl.parentElement?.classList.contains('input-group')) {
                    const wrapEl = document.createElement('div');
                    wrapEl.classList.add('input-group');
                    wrapEl.append(fieldEl);
                }

                const inputGroupEl = document.createElement('div');
                inputGroupEl.setAttribute('class', 'input-group-append clear');
                fieldEl.after(inputGroupEl);

                const clearBtnEl = document.createElement('button');
                clearBtnEl.setAttribute('type', 'button');
                clearBtnEl.setAttribute('class', 'button');
                clearBtnEl.classList.add('btn btn-outline-th-lightblue btn-clear');
                clearBtnEl.addEventListener('click', (ev: MouseEvent) => {
                    fieldEl.value = '';
                });
                inputGroupEl.append(clearBtnEl);
             }

            // Value picker
            if(
                fieldEl.type != 'range'
                && ( fieldEl.classList.contains('value-picker') || fieldEl.getAttribute('list') )
                && fieldEl.closest<HTMLElement>('.form-element')?.querySelector<HTMLFormElement>('datalist')
            ) {
                this.processState.widgets.push({
                    identifier: 'valuePicker',
                    field: fieldEl
                });
            }

            // Counter widget
            if(
                (
                    (fieldEl.getAttribute('minlength') && parseInt(fieldEl.getAttribute('minlength') ?? '0') > 0)
                    || (fieldEl.dataset.minlength && parseInt(fieldEl.dataset.minlength ?? '0') > 0)
                    || (fieldEl.getAttribute('maxlength') && parseInt(fieldEl.getAttribute('maxlength') ?? '0') > 0)
                    || (fieldEl.dataset.maxlength && parseInt(fieldEl.dataset.maxlength ?? '0') > 0)
                )
                && !fieldEl.classList.contains('disable-counter')
                && parseInt(fieldEl.dataset.disableCounter ?? '0') < 1
            ) {
                this.processState.widgets.push({
                    identifier: 'counter',
                    field: fieldEl
                });
            }

            // Widgets
            if(fieldEl.dataset.widget && fieldEl.dataset.widget != '') {
                const widget: {[key: string]: any } = JSON.parse(decodeURIComponent(fieldEl.dataset.widget));
                widget.field = fieldEl;
                this.processState.widgets.push(widget);
            }

            const widgets: any | null = fieldEl.dataset.widgets ? JSON.parse(decodeURIComponent(fieldEl.dataset.widgets)) : null;
            if(widgets) {
                if(Array.isArray(widgets)) {
                    widgets.forEach((widget) => {
                        widget.field = fieldEl;
                        this.processState.widgets.push(widget);
                    });
                } else {
                    widgets.field = fieldEl;
                    this.processState.widgets.push(widgets);

                }
            }

            // Prepend validation
            let listeners: string[] = [];
            if(fieldEl.dataset.validateOnChange && parseInt(fieldEl.dataset.validateOnChange) > 0) {
                listeners.push('change');
            }
            if(fieldEl.dataset.validation) {
                const validationConfig: KeyValuePair = JSON.parse(fieldEl.dataset.validation);
                if(validationConfig) {
                    let validationListeners: string[] = [];
                    if(typeof(validationConfig.listeners) == 'string') {
                        validationListeners = validationConfig.listeners.split(' ');
                    } else if(typeof(validationConfig.listeners) == 'object') {
                        validationListeners = Object.values(validationConfig.listeners as object);
                    }
                    if(validationListeners.length > 0) {
                        validationListeners.forEach((listener) => {
                            if(listeners.indexOf(listener) < 0) {
                                listeners.push(listener);
                            }
                        });
                    }


                    const mergedValidators = new Map<string, Validator>();
                    const validators: Validator[] = (validationConfig.validators ?? []) as Validator[];
                    validators.forEach((validator) => {
                        mergedValidators.set(validator.identifier, validator);
                    });
                    const validation: Validation = {
                        validators: Array.from(mergedValidators.values())
                    }

                    fieldValidationMap.set(fieldEl, validation);
                    this.initValidators(fieldEl);

                }
            }

            if(listeners.length > 0) {
                listeners.forEach((eventName) => {
                    fieldEl.addEventListener(eventName, () => {
                        let isValid = this.validate(fieldEl);
                        if(!isValid) {
                            window.setTimeout(() => {
                                fieldEl.focus();
                            }, 0);
                        }
                        return isValid;
                    });
                });
                if(fieldEl.value != '') {
                    this.validate(fieldEl);
                }
            }
        });

    }

    protected initValidators(fieldEl: HTMLInputElement) {
        const validation: {[key: string]: any} = JSON.parse(decodeURIComponent(fieldEl.dataset.validation ?? ''));
        if(validation && validation.validators && Object.keys(validation.validators).length > 0) {
            for(const identifier in validation.validators) {
                const validator = validation.validators[identifier];
                const options = (validator.options) ? validator.options : {};
                switch(identifier.toUpperCase()) {
                    case 'PASSWORDVERIFICATION':
                        let compareWith = this.form.querySelector<HTMLElement>(options.compareWithSelector);
                        if(compareWith) {
                            compareWith.addEventListener('change', () => {
                                this.validate(fieldEl);
                            });
                            compareWith.addEventListener('input', () => {
                                this.validate(fieldEl);
                            });
                        }
                    break;
                }
            }
        }

    }

    protected validate(fieldEl: HTMLInputElement): boolean {
        let isValid = true;
        let hasCustomValidators = false;
        let messages: string[] = [];
        let validation: Validation = (fieldEl.dataset.validation && fieldEl.dataset.validation != '') ? JSON.parse(decodeURIComponent(fieldEl.dataset.validation)) : fieldValidationMap.get(fieldEl) ?? {};

        fieldEl.classList.remove('is-valid');
        fieldEl.classList.remove('is-invalid');
        fieldEl.classList.remove('error');
        fieldEl.removeAttribute('aria-invalid');
        fieldEl.removeAttribute('aria-describedby');
        fieldEl.removeAttribute('aria-errormessage');
        fieldEl.closest('.form-element')?.querySelectorAll<HTMLElement>('.invalid-feedback, .error.help-block').forEach((subEl: HTMLElement) => {
            subEl.remove();
        });

        if(validation && validation.validators && Object.keys(validation.validators).length > 0) {
            hasCustomValidators = true;
            for(const idx in validation.validators) {
                let value = fieldEl.value;
                const validator = validation.validators[idx];
                const identifier = validator.identifier;

                const options = validator.options ?? {};
                const minValue = parseInt(fieldEl.getAttribute('min') ?? options.min ?? '0');
                const maxValue = parseInt(fieldEl.getAttribute('max') ?? options.max ?? '0');
                switch(identifier.toUpperCase()) {
                    case 'PASSWORDVERIFICATION':
                        const compareWithEl = this.form.querySelector<HTMLInputElement>(options.compareWithSelector);
                        if(compareWithEl) {
                            if(compareWithEl.value == '' || value !== compareWithEl.value) {
                                isValid = false;
                                messages.push(window.ThiemeCore.Lang.translate('common:validator.passwordVerification.notvalid'));
                            }
                        } else {
                            isValid = false;
                            messages.push(window.ThiemeCore.Lang.translate('common:validator.passwordVerification.notvalid'));
                        }
                    break;
                    case 'EQUALS':
                        let compareWith = options.compareWith;
                        if(options.compareWithSelector && options.compareWithSelector != '') {
                            const el = this.form.querySelector<HTMLInputElement>(options.compareWithSelector);
                            if(el) {
                                compareWith = el.value ?? '';
                            }
                        }
                        if(value !== compareWith) {
                            isValid = false;
                            messages.push(window.ThiemeCore.Lang.translate('common:validator.equals.notvalid', { source: value, dest: compareWith }));
                        }
                    break;
                    case 'NOTEMPTY':
                        if(value.replace(/^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g, '') == '') {
                            isValid = false;
                            messages.push(window.ThiemeCore.Lang.translate('common:validator.notEmpty.notvalid'));
                        }
                    break;
                    case 'STRINGLENGTH':
                        if(minValue > 0 && maxValue > 0) {
                            if(value.length < minValue || value.length > maxValue) {
                                isValid = false;
                                messages.push(window.ThiemeCore.Lang.translate('common:validator.stringLength.between', { max: maxValue.toString(), min: minValue.toString() }));
                            }
                        } else {
                            if(minValue > 0 && value.length < minValue) {
                                isValid = false;
                                messages.push(window.ThiemeCore.Lang.translate('common:validator.stringLength.less', { min: minValue.toString() }));
                            }
                            if(maxValue && value.length > maxValue) {
                                isValid = false;
                                messages.push(window.ThiemeCore.Lang.translate('common:validator.stringLength.exeed', { max: maxValue.toString() }));
                            }
                        }
                    break;
                    case 'NUMBERSONLY':
                        if(!value.match(/\d+/)) {
                            isValid = false;
                            messages.push(window.ThiemeCore.Lang.translate('common:validator.numbersOnly.notvalid'));
                        }
                    break;
                    case 'NUMBERRANGE':
                        if(value == '') {
                            isValid = false;
                            messages.push(window.ThiemeCore.Lang.translate('common:validator.numberRange.notvalid'));
                        } else {
                            if(parseInt(value) < minValue || parseInt(value) > maxValue) {
                                isValid = false;
                                messages.push(window.ThiemeCore.Lang.translate('common:validator.numberRange.range', { max: maxValue.toString(), min: minValue.toString() }));
                            }
                        }
                    break;
                    case 'INT':
                    case 'INTEGER':
                        if(!Number.isInteger(value)) {
                            isValid = false;
                            messages.push(window.ThiemeCore.Lang.translate('common:validator.integer.notvalid'));
                        }
                    break;
                    case 'FLOAT':
                        if(Math.ceil(parseFloat(value)) < parseFloat(value)) {
                            isValid = false;
                            messages.push(window.ThiemeCore.Lang.translate('common:validator.float.notvalid'));
                        }
                    break;
                    case 'BOOL':
                    case 'BOOLEAN':
                        if(typeof(options.is) != 'undefined') {
                            const expected = (options.is === 'true' || parseInt(options.is) > 0) ? true : false;
                            let check = (value === 'true' || parseInt(value) === 1) ? true : false;
                            if(expected) {
                                if(check !== expected) {
                                    isValid = false;
                                    messages.push(window.ThiemeCore.Lang.translate('common:validator.boolean.nottrue'));
                                }
                            } else {
                                if(check !== expected) {
                                    isValid = false;
                                    messages.push(window.ThiemeCore.Lang.translate('common:validator.boolean.notfalse'));
                                }
                            }
                        }
                    break;
                    case 'REGULAREXPRESSION':
                        if(options.pattern == '') {
                            isValid = false;
                            messages.push(window.ThiemeCore.Lang.translate('common:validator.regularExpression.empty'));
                        } else {
                            try {
                                if(!value.match(options.pattern)) {
                                    isValid = false;
                                    messages.push(window.ThiemeCore.Lang.translate('common:validator.regularExpression.nomatch'));
                                }
                            } catch (ex) {
                                isValid = false;
                                messages.push(window.ThiemeCore.Lang.translate('common:validator.regularExpression.error'));
                            }
                        }
                    break;
                    case 'EMAILADDRESS':
                        if(options && options.notEmpty && value == '') {
                            isValid = false;
                            messages.push(window.ThiemeCore.Lang.translate('common:validator.emailAddress.notvalid'));
                        } else {
                            if (/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(value) === false) {
                                isValid = false;
                                messages.push(window.ThiemeCore.Lang.translate('common:validator.emailAddress.notvalid'));
                            }
                        }
                    break;
                    case 'FILETYPE':
                        if(fieldEl.files && fieldEl.files.length > 0) {
                            Array.from<File>(fieldEl.files).forEach((file: File) => {
                                if((','+options.accept+',').search(','+file.type+',') < 0) {
                                    const allowedMimeTypes: string[] = options.accept.split(',');
                                    const allowed: string[] = allowedMimeTypes.map((mimeType: string) => {
                                        return window.ThiemeCore.Lang.translate('common:mimeTypes.'+mimeType);
                                    });
                                    messages.push(
                                        window.ThiemeCore.Lang.translate('common:validator.file.notaccepted', { allowed: allowed.join(', ') })
                                    );
                                    isValid = false;
                                }
                            });
                        }
                    break;
                    case 'FILESIZE':
                        if(fieldEl.files && fieldEl.files.length > 0) {
                            Array.from<File>(fieldEl.files).forEach((file: File) => {
                                const maxSize = parseInt(options.maxSize);
                                if(file.size > maxSize) {
                                    messages.push(
                                        window.ThiemeCore.Lang.translate('common:validator.file.wrongsize', { max: this.convertBytesToSize(maxSize) })
                                    );
                                    isValid = false;
                                }
                            });
                        }
                    break;
                    case 'FILEUPLOAD':
                        const fileUploadValidator = <FileUploadValidator>validator;
                        if(fieldEl.hasAttribute('required')) {
                            fieldEl.required = true;
                        }
                        if(fieldEl.files && fieldEl.files.length > 0) {
                            if(fieldEl.hasAttribute('required')) {
                                fieldEl.required = false;
                            }
                            Array.from<File>(fieldEl.files).forEach((file: File) => {
                                const maxSize = fileUploadValidator.fileSize;
                                const accept = fileUploadValidator.accept;
                                if(maxSize > 0 && file.size > maxSize) {
                                    messages.push(
                                        window.ThiemeCore.Lang.translate('common:validator.file.wrongsize', { max: this.convertBytesToSize(maxSize) })
                                    );
                                    isValid = false;
                                }
                                if(accept !== '' && (',' + accept + ',').search(',' + file.type + ',') < 0) {
                                    const allowedMimeTypes: string[] = accept.split(',');
                                    const allowed: string[] = allowedMimeTypes.map((mimeType: string) => {
                                        return window.ThiemeCore.Lang.translate('common:mimeTypes.'+mimeType);
                                    });
                                    messages.push(
                                        window.ThiemeCore.Lang.translate('common:validator.file.notaccepted', { allowed: allowed.join(', ') })
                                    );
                                    isValid = false;
                                }
                            });
                        }
                    break;
                }
            }
        }

        const validity = fieldEl.validity;
        if(validity && !validity.valid) {
            isValid = false;
            if(fieldEl.validationMessage) {
                messages.push(fieldEl.validationMessage);
            }
        }
        if(isValid) {
            if(hasCustomValidators) {
                fieldEl.classList.add('is-valid');
            }
        } else {
            const feedbackId = 'invalid-feedback-' + Math.random().toString(16).slice(2);
            fieldEl.classList.add('is-invalid');
            fieldEl.setAttribute('aria-invalid', 'true');
            fieldEl.setAttribute('aria-describedby', feedbackId);
            fieldEl.setAttribute('aria-errormessage', feedbackId);

            const feedbackEl = document.createElement('div');
            feedbackEl.setAttribute('class', 'invalid-feedback');
            feedbackEl.innerHTML = messages.join('<br/>');

            const feedbackContainerEl = fieldEl.parentElement?.querySelector('.feedback-container');
            if(feedbackContainerEl) {
                const feedbackSpecialEl = document.createElement('span');
                feedbackSpecialEl.setAttribute('id', feedbackId);
                feedbackSpecialEl.innerHTML = feedbackEl.innerHTML;
                feedbackContainerEl.appendChild(feedbackSpecialEl);
            } else {
                feedbackEl.setAttribute('id', feedbackId);
            }

            if(fieldEl.parentElement?.classList.contains('input-group')) {
                fieldEl.parentElement?.appendChild(feedbackEl);
            } else {
                fieldEl.after(feedbackEl);
            }


        }

        return isValid;
    }

    protected renderWidgets(widgets: any[]) {
        for(const key in widgets) {
            const widget = widgets[key];
            if(typeof(widget.identifier) == 'undefined') {
                continue;
            }
            const fieldEl: HTMLInputElement = widget.field as HTMLInputElement;
            switch(widget.identifier.toUpperCase()) {
                case 'DATEPICKER':
                    import('./widget/date-picker-widget.js').then((module) => {
                        widget.locale = this.options.locale;
                        widget.format = widget.format ?? 'Y-m-d H:i';
                        widget.forceBrowserNative = TypeConverter.convertTo<boolean>(fieldEl.dataset.forceNative, TypeConverter.ConvertType.BOOLEAN, false);
                        if(fieldEl.dataset.format) {
                            widget.format = fieldEl.dataset.format;
                        }
                        if(fieldEl.dataset.dateFormat) {
                            widget.format = fieldEl.dataset.dateFormat;
                        }
                        module.default.getOrCreateInstance(fieldEl, widget)?.process();
                    });
                break;
                case 'SHOWHIDEPASSWORD':
                    ShowPasswordWidget.getOrCreateInstance(fieldEl, widget)?.process();
                break;
                case 'PASSWORDSTRENGTH':
                    PasswordStrengthWidget.getOrCreateInstance(fieldEl, widget)?.process();
                break;
                case 'COUNTER':
                    CounterWidget.getOrCreateInstance(fieldEl, widget)?.process();
                break;
                case 'RTEEDITOR':
                case 'CKEDITOR':
                    import('./widget/rte-editor-widget.js').then((module) => {
                        module.default.getOrCreateInstance(fieldEl, widget)?.process();
                    });
                break;
                case 'VALUEPICKER':
                    ValuePickerWidget.getOrCreateInstance(fieldEl, widget)?.process();
                break;
                case 'RANGE':
                    RangeWidget.getOrCreateInstance(fieldEl, widget)?.process();
                break;
                case 'RATING':
                case 'GRADEING':
                    RatingGradingWidget.getOrCreateInstance(fieldEl, widget)?.process();
                break;
                case 'SELECTLIST':
                    SelectListWidget.getOrCreateInstance(fieldEl, widget)?.process();
                break;
                case 'FILEUPLOAD':
                    FileUploadWidget.getOrCreateInstance(fieldEl, widget)?.process();
                break;
            }
        }

    }

    // Convert bytes to size
    protected convertBytesToSize(bytes: number) {
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        if (bytes === 0) return 'n/a';
        const size = Math.floor(Math.log(bytes) / Math.log(1024));
        const i = parseInt(size.toString());
        if (i === 0) return bytes+' '+sizes[i];
        return (bytes / (1024 * i)).toFixed(1)+' '+sizes[i];
    }

}

Form.initializeDom('form', true, false, true);

export type { FormConfig };
export default Form;
